import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import debounce from 'lodash/debounce';
import { FilterBar, useFilterBarStyles } from '../ui';
import { ORDER_STATUSES, orderStatus } from '../../lib/orderStatus';
import { MIN_DATE_SENTINEL, dateParam, digitsOnly } from '../../lib/orderFilters';

const HOLD_OPTIONS = [
    { label: 'On hold', value: '1' },
    { label: 'Not on hold', value: '0' },
];

interface OrdersFiltersProps {
    queryParams: any;
    loadOrders: (params: any) => void;
    loading: boolean;
    /** Resets every filter; the parent remounts this component afterwards so the search box empties. */
    onClear: () => void;
}

/** Filter strip of the orders list. Every change reloads page 1 with the new params. */
export default function OrdersFilters({ queryParams, loadOrders, loading, onClear }: OrdersFiltersProps) {
    const classes = useFilterBarStyles();
    const [search, setSearch] = useState<string>(() => digitsOnly(String(queryParams?.search ?? '')));
    const [searchError, setSearchError] = useState<string | null>(null);

    // The debounced search reads the latest params through a ref, so it is created once and never applies stale filters.
    const latest = useRef({ queryParams, loadOrders });
    latest.current = { queryParams, loadOrders };

    const applyFilter = (params: any, name: string, value: any) => {
        const next = { ...params, page: 1, [name]: value };
        if (name === 'maxDate' && !next.minDate) next.minDate = MIN_DATE_SENTINEL;
        if (name === 'minDate' && !next.maxDate) next.maxDate = Dayjs(new Date()).format('YYYY-MM-DD');
        latest.current.loadOrders(next);
    };
    const handleFilterChange = (name: string, value: any) => applyFilter(latest.current.queryParams, name, value);

    const debouncedSearch = useMemo(() => debounce((value: string) => {
        if (value.length === 0) {
            setSearchError(null);
            applyFilter(latest.current.queryParams, 'search', value);
        } else if (value.length < 3) {
            setSearchError('Type at least 3 digits');
        } else {
            setSearchError(null);
            applyFilter(latest.current.queryParams, 'search', value);
        }
    }, 500), []);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    // Order ids are numbers: letters and symbols are dropped as they are typed or pasted.
    const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const digits = digitsOnly(event.target.value);
        setSearch(digits);
        debouncedSearch(digits);
    };

    const onDateChange = (name: 'minDate' | 'maxDate') => (value: Dayjs.Dayjs | null) => {
        const iso = dateParam(value);
        if (iso) handleFilterChange(name, iso);
    };

    const statusValue: number[] = Array.isArray(queryParams?.orderStatusId) ? queryParams.orderStatusId : [];
    const minDate = queryParams?.minDate && queryParams.minDate !== MIN_DATE_SENTINEL ? Dayjs(queryParams.minDate) : null;
    const maxDate = queryParams?.maxDate ? Dayjs(queryParams.maxDate) : Dayjs(new Date());

    return (
        <FilterBar label="Order filters">
            <TextField
                className={classes.search}
                size="small"
                label="Search by ID"
                name="search"
                value={search}
                disabled={loading}
                onChange={onSearchChange}
                error={Boolean(searchError)}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'aria-describedby': searchError ? 'orders-search-hint' : undefined }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />
            <FormControl className={classes.select} size="small">
                <InputLabel id="orders-status-label">Status</InputLabel>
                <Select
                    labelId="orders-status-label"
                    label="Status"
                    name="orderStatusId"
                    multiple
                    value={statusValue}
                    disabled={loading}
                    onChange={(event) => handleFilterChange('orderStatusId', event.target.value)}
                    renderValue={(selected) => (selected.length === 1 ? orderStatus(selected[0]).label : `${selected.length} statuses`)}
                >
                    {ORDER_STATUSES.map((status) => (
                        <MenuItem key={status.id} value={status.id}>{status.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl className={classes.select} size="small">
                <InputLabel id="orders-hold-label">Hold</InputLabel>
                <Select
                    labelId="orders-hold-label"
                    label="Hold"
                    name="onHold"
                    value={queryParams?.onHold ?? ''}
                    disabled={loading}
                    onChange={(event) => handleFilterChange('onHold', event.target.value)}
                >
                    <MenuItem value="">Any</MenuItem>
                    {HOLD_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    className={classes.date}
                    label="From"
                    format="YYYY-MM-DD"
                    value={minDate}
                    maxDate={maxDate}
                    disabled={loading}
                    onChange={onDateChange('minDate')}
                    slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                    className={classes.date}
                    label="To"
                    format="YYYY-MM-DD"
                    value={maxDate}
                    maxDate={Dayjs(new Date())}
                    disabled={loading}
                    onChange={onDateChange('maxDate')}
                    slotProps={{ textField: { size: 'small' } }}
                />
            </LocalizationProvider>
            <Button className={classes.clear} variant="text" size="small" onClick={onClear} disabled={loading}>
                Clear filters
            </Button>
            {searchError && <p id="orders-search-hint" className={classes.hint} role="alert">{searchError}</p>}
        </FilterBar>
    );
}
